import { useState, useEffect, useCallback } from 'react';
import { SlotUI } from './components/SlotUI';
import { perfLog } from './perfLog';

/**
 * Demo app — shows how a consumer wires state and callbacks into <SlotUI />.
 * The module itself is state-agnostic; this file is NOT part of the public bundle.
 */

// Temporary diagnostic for the in-app-browser gate (see webviewDetect.ts):
// some in-app browsers (Telegram/Viber especially) don't reliably expose a
// distinguishable user-agent, and there's no way to inspect it from a
// phone's devtools inside those apps. Appending `?debugUA` to the URL
// shows the raw string full-screen so it can be read/screenshotted and
// used to add a proper detection token. Remove once Telegram/Viber's real
// UA strings are confirmed and added to KNOWN_IN_APP_TOKENS.
function useDebugUA(): string | null {
  const [show] = useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('debugUA'),
  );
  return show ? navigator.userAgent : null;
}

function DebugUAOverlay({ ua }: { ua: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        background: '#000',
        color: '#0F0',
        fontFamily: 'monospace',
        fontSize: 15,
        lineHeight: 1.5,
        padding: 20,
        overflowWrap: 'break-word',
        userSelect: 'text',
        WebkitUserSelect: 'text',
      }}
    >
      {ua}
    </div>
  );
}

export default function App() {
  const debugUA = useDebugUA();
  const [reelsUrls, setReelsUrls] = useState<string[]>([]);

  useEffect(() => {
    perfLog('App mounted, config fetch starting');
    const file = import.meta.env.DEV ? 'reels.config.dev.json' : 'reels.config.json';
    fetch(file)
      .then(r => r.json())
      .then(cfg => {
        perfLog('config fetch resolved, setReelsUrls about to run');
        setReelsUrls(cfg.urls ?? []);
      });
  }, []);

  // index.html's static `#initial-preloader` is no longer torn down here.
  // It used to be removed the instant `reelsUrls` populated, right as
  // SlotUI's own <Preloader/> mounted underneath to take over — but that
  // handoff was the source of a visible flicker, since the two preloaders'
  // markup had drifted apart (see the comment in index.html and
  // initialPreloaderBridge.ts). Now the static preloader stays up for the
  // *entire* load and hides itself once the game reports loaded — SlotUI's
  // `preloader` prop is intentionally left off below so it never mounts a
  // second overlay underneath this one.
  useEffect(() => {
    if (reelsUrls.length) perfLog('reelsUrls populated, SlotUI mounting');
  }, [reelsUrls.length]);

  const initialReelsUrl = reelsUrls[0] ?? '';
  const nextReelsUrlFactory = useCallback(
    (index: number): string => {
      if (reelsUrls.length <= 1) return '';
      return reelsUrls[(index + 1) % reelsUrls.length];
    },
    [reelsUrls],
  );

  const [bet, setBet] = useState(300);
  // Balance / win are driven by the game via `game.balanceUpdate` and
  // `game.winUpdate`. The initial values are placeholders that get
  // overwritten once the first reel reports its real numbers.
  const [balance, setBalance] = useState(400);
  const [win, setWin] = useState(0);
  const [auto, setAuto] = useState(0);
  const [freespins, setFreespins] = useState(0);
  // Bet options are sourced from the game — either via `game.init` on first
  // load or `game.betValueAndStepsUpdate` whenever the game changes them.
  // Undefined until the game reports something, in which case BetModal
  // falls back to its default option list.
  const [betOptions, setBetOptions] = useState<number[] | undefined>(undefined);
  // `spinning` flips Spin ↔ Stop on the SpinButton. In tiktokMode it's
  // driven by the game's `emitSpinStart/Stop` signals; the local `spin()`
  // fallback below also flicks it briefly so the icon animation works
  // even when no game is driving the shell.
  const [spinning, setSpinning] = useState(false);
  // `betEnabled` gates the BetModal — Playmer flips this off during
  // free spins (`disableBetChangeButtons`) so the player can't tamper
  // with the bet mid-round.
  const [betEnabled, setBetEnabled] = useState(true);
  // Gates the whole spin control cluster. Playmer flips this off via
  // `game.disableSpinButton` when a bonus round wants exclusive control
  // of the reel canvas (hides Auto/Bet, dims Spin, taps pass through).
  const [spinButtonEnabled, setSpinButtonEnabled] = useState(true);
  // Turbo mode — toggled by long-pressing the spin button. SlotUI
  // bridges `ui.turboChange` into the active reel so Playmer can honour
  // the mode inside the game loop; this state keeps the UI badge in sync.
  const [turbo, setTurbo] = useState(false);

  if (debugUA) return <DebugUAOverlay ua={debugUA} />;

  if (!reelsUrls.length) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        padding: 0,
        fontFamily:
          "'Oswald', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: 'hidden',
      }}
    >
      <SlotUI
        fullscreen
        tiktokMode
        theme="reelay"
        title="Save the Dead 2"
        reelsUrl={initialReelsUrl}
        nextReelsUrlFactory={nextReelsUrlFactory}
        bet={bet}
        balance={balance}
        win={win}
        auto={auto}
        freespins={freespins}
        betOptions={betOptions}
        spinning={spinning}
        betEnabled={betEnabled}
        spinButtonEnabled={spinButtonEnabled}
        turbo={turbo}
        onBetChange={setBet}
        onAutoChange={setAuto}
        onTurboChange={setTurbo}
        // Inbound game→shell sync. SlotUI listens for `game.*` messages
        // from the active reel iframe and drives these setters so the UI
        // reflects the game's truth after every spin / free spin / bet change.
        onBalanceUpdate={setBalance}
        onWinUpdate={setWin}
        onFreespinsUpdate={setFreespins}
        onBetOptionsUpdate={setBetOptions}
        onSpinningChange={setSpinning}
        onAutoStateChange={setAuto}
        onBetChangeButtonsEnabled={setBetEnabled}
        onSpinButtonEnabledChange={setSpinButtonEnabled}
        // Deliberately no `preloader` prop — index.html's static
        // `#initial-preloader` covers the entire loading window now (see
        // initialPreloaderBridge.ts), so SlotUI's own React <Preloader/>
        // would just be a second, redundant overlay underneath it.
      />
    </div>
  );
}
